import type { DoublePick, Outcome, ProbabilityPick } from "../../shared/types";
import { formatKickoff, formatPercent, formatSignedPercent, formatStake } from "../lib/format";
import { Badge, type BadgeTone } from "./Badge";
import { ConfidenceChip } from "./ConfidenceChip";
import { DoubleProvenance } from "./PickProvenance";
import { Stat } from "./Stat";
import { VerdictBadge } from "./VerdictBadge";

const OUTCOME_LABELS: Record<Outcome, string> = {
  home: "Home",
  draw: "Draw",
  away: "Away",
};

export function DoubleRow({ double, rank }: { double: DoublePick; rank: number }) {
  const [legA, legB] = double.legs;
  // Prefer model combined EV (independent signal) over consensus over raw fair EV.
  const ev = double.modelCombinedEv ?? double.combinedConsensusEv ?? double.combinedEv;
  const evTone = evBadgeTone(double.modelCombinedEv ?? double.combinedConsensusEv);
  const evLabel = double.modelCombinedEv !== undefined ? "MODEL EV" : "EV";

  return (
    <details
      className={`panel group/row overflow-hidden ${double.combinedHighConviction ? "ring-2 ring-emerald-400" : ""}`}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 px-2.5 py-1.5 sm:gap-3 sm:px-3 [&::-webkit-details-marker]:hidden">
        <span className="w-6 shrink-0 text-center text-xs font-medium text-ink-500">#{rank}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5 pb-1">
            <VerdictBadge verdict={double.combinedVerdict} />
            <ConfidenceChip confidence={double.combinedConfidence} />
            {evTone ? (
              <Badge
                tone={evTone}
                title="Expected profit or loss per €1 staked across the whole double. We prefer our independent model EV when available, otherwise sharp-book consensus."
              >
                {formatSignedPercent(ev)} {evLabel}
              </Badge>
            ) : null}
          </div>
          <LegLine leg={legA} />
          <LegLine leg={legB} />
        </div>
        <div className="flex shrink-0 items-center gap-2 text-right text-xs sm:gap-4">
          <Stat
            label="Odds"
            value={double.combinedDecimalOdds.toFixed(2)}
            description="Combined payout per €1 staked. Both legs must win to collect. 3.50 means €1 in → €3.50 back (€2.50 profit). Doubles always pay more than singles because the chance is lower."
          />
          <Stat
            className="hidden sm:block"
            label="Fair"
            value={double.combinedFairOdds.toFixed(2)}
            description="What the combined payout would be without bookmaker margin on either leg."
          />
          <Stat
            label="Joint"
            value={formatPercent(double.combinedFairProbability)}
            description="Estimated chance that BOTH legs win, assuming the two matches are independent. We checked the historical correlation and it's ≈ 0, so this assumption is safe."
          />
          <Stat
            className="hidden sm:block"
            label={evLabel}
            value={formatSignedPercent(ev)}
            description="Expected profit or loss per €1 staked across the double. We prefer our independent model EV (no bookmaker prices in the loop) when available, otherwise sharp-book consensus EV."
          />
          {double.modelCombinedFairProbability !== undefined ? (
            <Stat
              className="hidden md:block"
              label="Model"
              value={formatPercent(double.modelCombinedFairProbability)}
              description="Joint probability from our independent Dixon-Coles model (no bookmaker prices in the loop), assuming the two matches are independent (the backtest confirmed this assumption holds)."
            />
          ) : null}
          <Stat
            label="Stake"
            value={formatStake(double.kellyStakeFraction)}
            description="Suggested fraction of your bankroll to risk, using 0.15× of full Kelly to absorb estimation noise. Stake is sized off the model probability when available, otherwise consensus, otherwise no-vig. Dash (—) means negative expected value: skip it."
          />
          <Chevron />
        </div>
      </summary>
      <DoubleProvenance double={double} />
    </details>
  );
}

function LegLine({ leg }: { leg: ProbabilityPick }) {
  return (
    <p className="flex min-w-0 items-baseline gap-1.5 text-sm">
      <span className="shrink-0 rounded bg-ink-100 px-1 py-0.5 text-[10px] font-medium text-ink-700">
        {OUTCOME_LABELS[leg.outcome]}
      </span>
      <span className="truncate font-semibold text-ink-900">{leg.selectionName}</span>
      <span className="shrink-0 text-[11px] text-ink-500">@ {leg.decimalOdds.toFixed(2)}</span>
      {leg.leagueId ? (
        <span className="hidden shrink-0 text-[11px] uppercase tracking-wide text-ink-500 sm:inline">
          · {leg.leagueId}
        </span>
      ) : null}
      <span className="hidden truncate text-[11px] text-ink-500 sm:inline">
        · {formatKickoff(leg.match.kickoffTime)}
      </span>
    </p>
  );
}

function Chevron() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      className="ml-0.5 h-3 w-3 shrink-0 text-ink-500 transition-transform group-open/row:rotate-180"
    >
      <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function evBadgeTone(ev: number | undefined): BadgeTone | null {
  if (ev === undefined) return null;
  if (ev > 0.005) return "good";
  if (ev < -0.05) return "bad";
  return "neutral";
}
